// LAP refonte Mes Objectifs — page en cartes avec progression + section Focus.
// * Les filtres arrivent par l'URL (relayés tels quels à l'API : les cartes
//   SmartFeed pointent /mesobjectifs?linked=none, ?status=Engagé, ?echeance=…).
// * Chips de filtre rapide en tête (Tous / Engagés / En cours / Non liés /
//   Réussis) qui NAVIGUENT (l'URL reste la source de vérité).
// * makeGetAllRequest : la page charge TOUTE la collection (piège PAGE_SIZE=20).
import { Box, Chip, Container } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { makeGetAllRequest, makeGetRequest, makePostRequest } from '../../utils/api'
import urls from '../../utils/urls'
import CreateObjectifsForm from './CreateObjectifsForm'
import ListObjectifs from './ListObjectifs'

// Filtres rapides — chaque chip navigue vers une query relayée à l'API
// (mêmes filtres que les destinations des cartes SmartFeed).
const QUICK_FILTERS = [
  { key: 'all', query: '' },
  { key: 'engaged', query: 'status=Engagé' },
  { key: 'inProgress', query: 'status=En cours' },
  { key: 'unlinked', query: 'linked=none' },
  { key: 'achieved', query: 'status=reussite' },
]

export const MesOjectifs = () => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const navigate = useNavigate()
  const location = useLocation()

  const [objectifs, setObjectifs] = useState({})
  const [objectifsMetadata, setObjectifsMetaDeta] = useState({})
  const [linkables, setLinkables] = useState(null)
  const [editObjectif, setEditObjectif] = useState(null)
  const [openWorkTimeFromUrl, setOpenWorkTimeFromUrl] = useState(false)
  const [openFromAdvisor, setOpenFromAdvisor] = useState(false) // LAP-CP05

  useEffect(() => {
    fetchMesObjectifsMetaData()
    fetchLinkables()
  }, [])

  useEffect(() => {
    fetchObjectifs()
  }, [location.search])
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlAction = searchParams.get('action') || searchParams.get('modal')

    if (urlAction === 'new-work-time') {
      setOpenWorkTimeFromUrl(true)
    }
    // LAP-CP05 — Ouverture depuis le Conseiller Patrimonial avec un prefill
    // transmis via location.state.prefill. TODO : étendre CreateObjectifsForm
    // pour lire ce prefill et pré-remplir le dialog (domain, montant, périodicité).
    if (urlAction === 'new-from-advisor') {
      setOpenFromAdvisor(true)
    }
  }, [location.search])

  const fetchObjectifs = async () => {
    // On retire les paramètres purement front (focus / action / modal) avant de
    // relayer la query à l'API — le reste (status, linked, echeance, domain…)
    // correspond aux filtres du ObjectifsFilter backend.
    const params = new URLSearchParams(location.search)
    params.delete('focus')
    params.delete('action')
    params.delete('modal')
    const qs = params.toString()
    const url = `${urls.crudMesObjectifs}${qs ? `?${qs}` : ''}`
    const response = await makeGetAllRequest(url, {}, true)
    if (response.status === 200) {
      const results = response.data.results || response.data || []
      setObjectifs({ results, count: results.length })
    }
  }

  const fetchLinkables = async () => {
    // Éléments rattachables (roadmap / projets / listes / tâches / événements)
    // pour le sélecteur de liaisons du popup.
    const response = await makeGetRequest(urls.mesObjectifsLinkables, {}, true)
    if (response.status === 200) {
      setLinkables(response.data)
    }
  }

  const fetchMesObjectifsMetaData = async () => {
    const response = await makeGetRequest(urls.fetchMesObjectifsMetaData, {}, true)
    if (response.status === 200) {
      const domains = []
      const frequency = []
      const predefinedObjectives = []
      const status = [
        { label: 'Engagé' },
        { label: 'En cours' },
        { label: 'Annulé' },
        { label: 'reussite' },
      ]
      response.data.domains.forEach((v) => {
        domains.push({ label: v.domain, id: v.id })
      })
      response.data.frequency.forEach((v) => {
        frequency.push({ label: v.name_of_periodicity, id: v.id })
      })
      response.data.predefined_objectives.forEach((v) => {
        predefinedObjectives.push({
          label: v.objectives_name,
          id: v.id,
          domain: v.domain,
          value: v.value_format,
        })
      })
      setObjectifsMetaDeta({
        domains,
        frequency,
        predefinedObjectives,
        status,
      })
    }
  }

  // Passage de statut d'une carte (réussite / Annulé) — réutilise l'endpoint
  // bulk existant (scopé user + status cible) puis recharge : le back peut
  // avoir promu un nouvel objectif « Engagé » (focus x3).
  const setObjectifStatus = async (objectif, status) => {
    const response = await makePostRequest(
      urls.bulkObjectifsAnnuler,
      { selected: [objectif.id], status },
      {},
      true,
    )
    if (response.status === 200) {
      fetchObjectifs()
    }
  }

  const currentQuery = location.search.replace(/^\?/, '')
  const isActiveFilter = (query) => {
    if (query === '') return currentQuery === ''
    return currentQuery === query
  }

  return (
    <Container maxWidth="xl">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        py={2}
      >
        {/* Filtres rapides (URL = source de vérité, relayée à l'API) */}
        <Box display="flex" gap={1} flexWrap="wrap">
          {QUICK_FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={t(`mesObjectifs.filter_${f.key}`)}
              onClick={() => navigate(f.query ? `?${f.query}` : location.pathname)}
              sx={{
                cursor: 'pointer',
                fontWeight: 600,
                bgcolor: isActiveFilter(f.query) ? '#f59e0b' : 'rgba(255,255,255,0.1)',
                color: isActiveFilter(f.query) ? '#0f172a' : 'white',
                '&:hover': { bgcolor: isActiveFilter(f.query) ? '#f59e0b' : 'rgba(255,255,255,0.2)' },
              }}
            />
          ))}
        </Box>
        <CreateObjectifsForm
          objectifsMetadata={objectifsMetadata}
          setObjectifs={setObjectifs}
          linkables={linkables}
          editObjectif={editObjectif}
          onEditConsumed={() => setEditObjectif(null)}
          onSaved={fetchObjectifs}
          openFromUrl={openWorkTimeFromUrl || openFromAdvisor /* LAP-CP05 */}
          selectedVal="Temps de travail"
          onUrlOpenConsumed={() => {
            setOpenWorkTimeFromUrl(false)
            setOpenFromAdvisor(false) // LAP-CP05
            navigate(location.pathname, { replace: true })
          }}
        />
      </Box>

      <ListObjectifs
        objectifs={objectifs}
        onEdit={(objectif) => setEditObjectif(objectif)}
        onSetStatus={setObjectifStatus}
      />
    </Container>
  )
}
